import { memo, useCallback } from 'react';
import AbstractBanner from '../AbstractBanner/AbstractBanner';
import { fireAlarmTypes, severityLevels } from './FireAlarmBannerUtils';
import styled from 'foundations-theming/utils';
import { get, set } from '../../../utils/tempStorage';
import getCurrentApp from '../../../utils/getCurrentApp';
import getCurrentUrlWithoutPortalId from '../../../utils/getCurrentUrlWithoutPortalId';
import { isSeleniumRunning } from '../../../utils/analytics/utils/testEnv';
import useUsageTracker from '../../../hooks/useUsageTracker';
import { useSelector } from 'react-redux';
import { getUser } from '../../../selectors';
import { getPortalId } from '../../../utils/getPortalId';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledTrackedLink = styled.a.withConfig({
  displayName: "FireAlarmBanner__StyledTrackedLink",
  componentId: "ofuawm-0"
})(({
  theme,
  type
}) => {
  const isCritSit = type === `${severityLevels.critSit}`;
  return Object.assign({
    marginInlineStart: theme.space['250'],
    color: isCritSit ? theme.color['text-core-on-fill-default'] : theme.color['fill-info-default'],
    fontWeight: isCritSit ? theme.typography['heading-100'].fontWeight : theme.typography['heading-200'].fontWeight,
    textDecoration: isCritSit ? 'underline' : 'none'
  }, isCritSit && {
    '&:hover': {
      color: theme.color['fill-tertiary-pressed']
    }
  });
});
const FireAlarmBanner = ({
  fireAlarm
}) => {
  const {
    severity,
    title,
    message,
    id,
    dismissible,
    callToAction,
    revisionNumber
  } = fireAlarm;
  const {
    track
  } = useUsageTracker();
  const portalId = getPortalId();
  const user = useSelector(getUser);
  const userId = user ? user.user_id : 0;
  const type = fireAlarmTypes[severity];
  const bannerId = `alarm.${id}.revision.${revisionNumber}`;
  const storageKey = `firealarm.dismiss.${bannerId}-${portalId}-${userId}`;
  const bannerRefCallback = useCallback(element => {
    if (element) {
      const bannerStatus = get(storageKey);
      const hasSeenOrDismissedBanner = ['seen', 'true'].includes(bannerStatus);

      // If banner has been seen or dismissed, dont resend NavUsageTracker or re-mark as seen
      if (!hasSeenOrDismissedBanner) {
        set(storageKey, 'seen');
        if (track && !isSeleniumRunning()) {
          track('fireAlarm', {
            currentApp: getCurrentApp(),
            currentUrl: getCurrentUrlWithoutPortalId(),
            type,
            title,
            id
          });
        }
      }
    }
  }, [track, title, type, id, storageKey]);
  const callToActionLink = callToAction ?
  /*#__PURE__*/
  // @bend-expect trellis-tokenized
  _jsx(StyledTrackedLink, {
    href: callToAction.url,
    type: severity,
    "data-test-id": `firealarm.callToAction.${bannerId}`,
    children: callToAction.ctaText
  }) : null;
  return /*#__PURE__*/_jsx(AbstractBanner, {
    isCloseable: dismissible,
    storageKey: storageKey,
    title: title,
    type: type,
    "data-test-id": bannerId,
    ref: bannerRefCallback,
    children: /*#__PURE__*/_jsxs(_Fragment, {
      children: [message, callToActionLink]
    })
  }, bannerId);
};
export default /*#__PURE__*/memo(FireAlarmBanner);